/**
 * MentionPopover Component
 * Filtered popover for @mentioning campaign members in the quick-add input.
 * Uses Radix Popover anchored to the input ref.
 */

import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { User } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useVolunteers } from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

export interface MentionPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (userId: string, displayName: string) => void;
  searchQuery: string;
  anchorRef: React.RefObject<HTMLInputElement | null>;
}

export interface MentionPopoverRef {
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

interface Measurable {
  getBoundingClientRect(): DOMRect;
}

// ============================================================================
// Helpers
// ============================================================================

/** Format name as "FirstName L." for chip display */
function toShortName(firstName: string, lastName: string): string {
  return lastName ? `${firstName} ${lastName[0]}.` : firstName;
}

// ============================================================================
// Component
// ============================================================================

export const MentionPopover = forwardRef(function MentionPopover(
  props: MentionPopoverProps,
  ref: React.ForwardedRef<MentionPopoverRef>
) {
  const { open, onOpenChange, onSelect, searchQuery, anchorRef } = props;
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const { data: volunteers = [] } = useVolunteers();
  const { user } = useAuth();

  // Filter volunteers by search query, exclude current user
  const filtered = volunteers
    .filter((v) => {
      if (v.userId === user?.id) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const full = `${v.firstName} ${v.lastName}`.toLowerCase();
      return (
        v.firstName.toLowerCase().startsWith(q) ||
        v.lastName.toLowerCase().startsWith(q) ||
        full.startsWith(q)
      );
    })
    .slice(0, 6);

  // Reset highlight when query or open state changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery, open]);

  // Clamp highlighted index when list shrinks
  useEffect(() => {
    if (highlightedIndex >= filtered.length) {
      setHighlightedIndex(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, highlightedIndex]);

  const handleSelect = useCallback(
    (volunteer: (typeof filtered)[0]) => {
      const displayName = toShortName(volunteer.firstName, volunteer.lastName);
      onSelect(volunteer.userId, displayName);
    },
    [onSelect]
  );

  // Keyboard handler — called from the parent input's onKeyDown
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || filtered.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (filtered[highlightedIndex]) {
            handleSelect(filtered[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    },
    [open, filtered, highlightedIndex, handleSelect, onOpenChange]
  );

  useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor virtualRef={anchorRef as React.RefObject<Measurable>} />
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-56 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
      >
        <div className="py-1 max-h-[240px] overflow-y-auto">
          {filtered.map((volunteer, index) => (
            <button
              key={volunteer.userId}
              type="button"
              onClick={() => handleSelect(volunteer)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer',
                'hover:bg-accent transition-colors',
                index === highlightedIndex && 'bg-accent'
              )}
            >
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="flex-1 text-left truncate font-medium">
                {volunteer.firstName} {volunteer.lastName}
              </span>
              <span className="text-xs text-muted-foreground capitalize">
                {volunteer.isOwner ? 'Owner' : (volunteer.presetKey ?? 'Volunteer')}
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {searchQuery ? 'No matches' : 'No team members'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});
